import { View } from "@react-pdf/renderer";
import { HtmlToPdf } from "@/components/PdfDocument/HtmlToPdf";
import {
    getSectionHeadingMarginYStyle,
    getSectionItemMarginYStyle,
} from "@/components/PdfDocument/utils/getStyles";
import {
    baseLiveResumeStyles,
    liveResumeStyles,
    liveSectionStyles,
} from "@/pages/LivePreview/liveResumeStyles";
import { ResumeStyles } from "@/types/resume";

const objectHasValue = (obj: object) => {
    return Object.values(obj).some(value => !!value);
};

interface FullWidthSectionItemProps {
    title?: string;
    description?: string;
    templateStyle: ResumeStyles;
    isLastItem?: boolean;
}

export const FullWidthSectionItem = ({
    title,
    description,
    templateStyle,
    isLastItem = false,
}: FullWidthSectionItemProps) => {
    const itemHasValues = objectHasValue({
        title,
        description,
    });

    if (!itemHasValues) return null;

    const titleHtml = title ? `<h3>${title}</h3>` : "";

    return (
        <View
            style={{
                ...liveResumeStyles.fullWidthSection,
                ...getSectionItemMarginYStyle(
                    templateStyle.GLOBAL.sectionSpacingSize,
                    isLastItem,
                ),
            }}
        >
            {titleHtml && (
                <HtmlToPdf
                    html={titleHtml}
                    documentStyles={templateStyle}
                />
            )}
            {description && (
                <HtmlToPdf
                    html={description}
                    documentStyles={templateStyle}
                />
            )}
        </View>
    );
};

interface WithDatesSectionItemProps {
    title?: string;
    subtitle?: string;
    additionalTitle?: string;
    datePeriod?: string;
    location?: string;
    description?: string;
    templateStyle: ResumeStyles;
    isLastItem?: boolean;
}

export const WithDatesSectionItem = ({
    title,
    subtitle,
    additionalTitle,
    datePeriod,
    location,
    description,
    templateStyle,
    isLastItem = false,
}: WithDatesSectionItemProps) => {
    const itemHasValues = objectHasValue({
        title,
        subtitle,
        additionalTitle,
        datePeriod,
        location,
        description,
    });

    if (!itemHasValues) return null;

    const titleHtml = title ? `<h3>${title}</h3>` : "";
    const subtitleHtml = subtitle ? `<h3>${subtitle}</h3>` : "";
    const additionalTitleHtml = additionalTitle
        ? `<p>${additionalTitle}</p>`
        : "";
    const datePeriodHtml = datePeriod
        ? `<p style="text-align: right;">${datePeriod}</p>`
        : "";
    const locationHtml = location
        ? `<p style="text-align: right;">${location}</p>`
        : "";

    const fullTitleHtml = `${titleHtml}${subtitleHtml}${additionalTitleHtml}`;

    return (
        <View
            style={{
                ...baseLiveResumeStyles.verticalFlex,
                ...getSectionItemMarginYStyle(
                    templateStyle.GLOBAL.sectionSpacingSize,
                    isLastItem,
                ),
            }}
        >
            {(fullTitleHtml || datePeriodHtml || locationHtml) && (
                <View style={liveSectionStyles.withDatesWrapper}>
                    {fullTitleHtml && (
                        <View style={liveSectionStyles.withDatesLeftContent}>
                            <HtmlToPdf
                                html={fullTitleHtml}
                                documentStyles={templateStyle}
                            />
                        </View>
                    )}
                    {(datePeriodHtml || locationHtml) && (
                        <View style={liveSectionStyles.withDatesRightContent}>
                            {datePeriodHtml && (
                                <HtmlToPdf
                                    html={datePeriodHtml}
                                    documentStyles={templateStyle}
                                />
                            )}
                            {locationHtml && (
                                <HtmlToPdf
                                    html={locationHtml}
                                    documentStyles={templateStyle}
                                />
                            )}
                        </View>
                    )}
                </View>
            )}
            {description && (
                <View>
                    <HtmlToPdf
                        html={description}
                        documentStyles={templateStyle}
                    />
                </View>
            )}
        </View>
    );
};

export const SectionTitle = ({
    title,
    center = false,
    templateStyle,
}: {
    title?: string;
    center?: boolean;
    templateStyle: ResumeStyles;
}) => {
    if (!title) return null;
    return (
        <View
            style={{
                ...liveResumeStyles.fullWidthSection,
                ...getSectionHeadingMarginYStyle(
                    templateStyle.GLOBAL.sectionSpacingSize,
                    false,
                ),
            }}
        >
            <HtmlToPdf
                html={`<h2 style="text-align: ${center ? "center" : "left"};">${title}</h2>`}
                documentStyles={templateStyle}
            />
        </View>
    );
};
